
import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MessageBannerProps {
  senderName: string;
  message: string;
  senderPhotoUrl?: string;
  senderAge?: number;
  senderPartnerAge?: number;
  senderCategory?: string;
  isVip?: boolean;
  onClose: () => void;
  onClick?: () => void;
  duration?: number;
}

// Helper per ottenere il colore dell'età in base alla categoria
const getAgeColor = (category?: string): string => {
  switch (category) {
    case 'Couple':
      return 'text-white';
    case 'Lady':
    case 'Lesbian':
    case 'Bisexual (F)':
    case 'Dom (F)':
    case 'Sub (F)':
    case 'Switch (F)':
    case 'Lover (F)':
      return 'text-pink-400';
    case 'Gentleman':
    case 'Gay':
    case 'Bisexual (M)':
    case 'Dom (M)':
    case 'Sub (M)':
    case 'Switch (M)':
    case 'Lover (M)':
      return 'text-blue-500';
    case 'Transgender':
      return 'text-purple-400';
    default:
      return 'text-white/80';
  }
};

export function MessageBanner({ 
  senderName, 
  message, 
  senderPhotoUrl, 
  senderAge,
  senderPartnerAge,
  senderCategory,
  isVip,
  onClose,
  onClick,
  duration = 4000 
}: MessageBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    // Mostra il banner con animazione
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, 10);
    
    // Nascondi automaticamente dopo duration
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);
    
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [duration, onClose]);

  const ageColorClass = getAgeColor(senderCategory);
  
  return (
    <div
      className={cn(
        'fixed left-1/2 -translate-x-1/2 z-[999] transition-all duration-300 ease-out cursor-pointer',
        isVisible ? 'top-[100px] translate-y-0 opacity-100' : 'top-[100px] -translate-y-full opacity-0'
      )}
      onClick={() => {
        onClick?.();
        onClose();
      }}
    >
      <div className="bg-[#202020] rounded-xl shadow-2xl border border-white/10 p-2 min-w-[280px] max-w-[340px]">
        <div className="flex items-center gap-2">
          {/* Avatar */}
          <div className="flex-shrink-0">
            {senderPhotoUrl ? (
              <img
                src={senderPhotoUrl}
                alt={senderName}
                className="w-10 h-10 rounded-full object-cover border border-white/20"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  const parent = (e.target as HTMLImageElement).parentElement;
                  if (parent) {
                    parent.innerHTML = `<div class="w-10 h-10 rounded-full bg-gray-500 flex items-center justify-center text-white text-sm font-bold border border-white/20">${senderName?.charAt(0)?.toUpperCase() || '?'}</div>`;
                  }
                }}
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold border border-white/20">
                {senderName?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
          </div>
          
          {/* Contenuto */}
          <div className="flex-1 min-w-0">
            {/* Prima riga: Username, VIP, Età */}
            <div className="flex items-center gap-1 mb-0.5">
              <span className="text-sm font-semibold text-white truncate capitalize">
                {senderName}
              </span>
              
              {/* VIP Badge */}
              {isVip && (
                <img
                  src="/assets/images/vip2.svg"
                  alt="VIP"
                  className="w-3 h-3 flex-shrink-0"
                />
              )}
              
              {/* Età */}
              {senderAge && (
                <span className={`text-xs font-medium flex-shrink-0 ${ageColorClass}`}>
                  {senderCategory === 'Couple' && senderPartnerAge ? (
                    <>
                      <span className="text-blue-500">{senderAge}</span>
                      <span className="text-white/60">/</span>
                      <span className="text-pink-400">{senderPartnerAge}</span>
                    </>
                  ) : (
                    senderAge
                  )}
                </span>
              )}
            </div>
            
            {/* Messaggio */}
            <p className="text-xs text-white/70 line-clamp-1">
              {message}
            </p>
          </div>
          
          {/* Pulsante chiudi */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsVisible(false);
              setTimeout(onClose, 300);
            }}
            className="flex-shrink-0 p-1 rounded-full hover:bg-white/10 transition-colors"
          >
            <X size={12} className="text-white/50" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Hook per gestire i banner dei messaggi
export function useMessageBanner() {
  const [banners, setBanners] = React.useState<Array<{
    id: string;
    senderName: string;
    message: string;
    senderPhotoUrl?: string;
    senderAge?: number;
    senderPartnerAge?: number;
    senderCategory?: string;
    isVip?: boolean;
    onClick?: () => void;
  }>>([]);

  const showMessageBanner = (
    senderName: string, 
    message: string, 
    senderPhotoUrl?: string,
    senderAge?: number,
    senderPartnerAge?: number,
    senderCategory?: string,
    isVip?: boolean,
    onClick?: () => void
  ) => {
    const id = Date.now().toString();
    setBanners(prev => [...prev, { 
      id, 
      senderName, 
      message, 
      senderPhotoUrl, 
      senderAge,
      senderPartnerAge,
      senderCategory,
      isVip,
      onClick 
    }]);
  };

  const dismissBanner = (id: string) => {
    setBanners(prev => prev.filter(b => b.id !== id));
  };

  const BannerContainer = () => (
    <>
      {banners.map(banner => (
        <MessageBanner
          key={banner.id}
          senderName={banner.senderName}
          message={banner.message}
          senderPhotoUrl={banner.senderPhotoUrl}
          senderAge={banner.senderAge}
          senderPartnerAge={banner.senderPartnerAge}
          senderCategory={banner.senderCategory}
          isVip={banner.isVip}
          onClose={() => dismissBanner(banner.id)}
          onClick={banner.onClick}
        />
      ))}
    </>
  );

  return {
    showMessageBanner,
    BannerContainer
  };
}
